/**
 * Earned Value Management — period-by-period PV/EV/AC entry, computed indices,
 * S-curve overlay and forecast at completion.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TrendingUp, RefreshCcw, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { toast } from 'sonner';
import PrintButton from './PrintButton';
import InfoTip from './InfoTip';

import { makeFmt } from './currency';
const fmtNull = (fn, n) => n == null ? '—' : fn(n);
const fmt = (n, d = 3) => n == null ? '—' : Number(n).toFixed(d);

const STATUS = {
  on_track: { label: 'On track', color: 'bg-emerald-600 text-white', icon: <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> },
  over_budget: { label: 'Over budget', color: 'bg-amber-500 text-white', icon: <AlertTriangle className="h-3.5 w-3.5 mr-1" /> },
  behind_schedule: { label: 'Behind schedule', color: 'bg-amber-500 text-white', icon: <AlertTriangle className="h-3.5 w-3.5 mr-1" /> },
  over_budget_behind_schedule: { label: 'Over & behind', color: 'bg-red-600 text-white', icon: <AlertTriangle className="h-3.5 w-3.5 mr-1" /> },
  no_data: { label: 'No data', color: 'bg-slate-400 text-white', icon: null },
};

export default function EvmAnalysis({ calculation }) {
  const baseFmt = useMemo(() => makeFmt(calculation), [calculation]);
  const fmt$ = (n) => fmtNull(baseFmt, n);
  const months = Math.max(1, Math.round(calculation?.project_duration_months || 6));
  const bac = calculation?.final_cost_usd || 0;

  // Initial PV uses an even spread; user adjusts in-table
  const [bacInput, setBacInput] = useState(bac);
  const [pv, setPv] = useState(() => evenCum(bac, months));
  const [ev, setEv] = useState(() => Array(months).fill(0));
  const [ac, setAc] = useState(() => Array(months).fill(0));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setBacInput(bac);
    setPv(evenCum(bac, months));
    setEv(Array(months).fill(0));
    setAc(Array(months).fill(0));
  }, [bac, months]);

  const compute = async () => {
    try {
      setLoading(true);
      const res = await ConstructionAPI.computeEvm({ bac: Number(bacInput || 0), pv, ev, ac });
      setResult(res);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'EVM computation failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (calculation) compute(); /* eslint-disable-next-line */ }, [calculation?.id]);

  const update = (setter) => (i, val) => setter(prev => prev.map((v, idx) => idx === i ? Number(val || 0) : v));

  const chartData = useMemo(() => {
    if (!result) return [];
    return result.series.map(s => ({ ...s }));
  }, [result]);

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to start tracking earned value.</CardContent></Card>;
  }
  const summary = result?.summary || {};
  const status = STATUS[summary.status] || STATUS.no_data;

  return (
    <div className="space-y-4 print-section" data-testid="evm-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-1.5">
          <TrendingUp className="h-4 w-4" />Earned Value Management
          <InfoTip>PV (Planned Value) is what you planned to spend by each month. EV is the budgeted value of work actually done. AC is what you actually paid. CPI/SPI tell you cost &amp; schedule efficiency, EAC forecasts total project cost.</InfoTip>
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={compute} disabled={loading}><RefreshCcw className="h-3.5 w-3.5 mr-1.5" />Recompute</Button>
          <PrintButton testid="evm-print-btn" />
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Pill label="BAC (Budget at Completion)" value={fmt$(summary.PV != null ? bacInput : bac)} accent="bg-slate-900" />
        <Pill label="EAC (Estimate at Completion)" value={fmt$(summary.EAC)} accent="bg-orange-600" />
        <Pill label="ETC (Estimate to Complete)" value={fmt$(summary.ETC)} accent="bg-amber-600" />
        <Pill label="VAC (Variance at Completion)" value={fmt$(summary.VAC)} accent={(summary.VAC ?? 0) >= 0 ? 'bg-emerald-700' : 'bg-red-600'} />
        <Pill label="Status" value={<span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs ${status.color}`}>{status.icon}{status.label}</span>} accent="bg-indigo-600" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Pill label="CPI (cost performance)"      value={fmt(summary.CPI, 3)} accent="bg-sky-600" />
        <Pill label="SPI (schedule performance)"  value={fmt(summary.SPI, 3)} accent="bg-blue-600" />
        <Pill label="CV (cost variance)"          value={fmt$(summary.CV)} accent={(summary.CV ?? 0) >= 0 ? 'bg-emerald-600' : 'bg-red-600'} />
        <Pill label="SV (schedule variance)"      value={fmt$(summary.SV)} accent={(summary.SV ?? 0) >= 0 ? 'bg-emerald-600' : 'bg-red-600'} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">EVM curves (cumulative)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={v => fmt$(v, { compact: true })} />
              <Tooltip formatter={(v) => fmt$(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="PV" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="EV" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="AC" stroke="#ef4444" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base inline-flex items-center gap-1.5">
            Period entry (cumulative USD)
            <InfoTip>Enter cumulative figures: each period&apos;s value should be ≥ the previous period.</InfoTip>
          </CardTitle>
          <p className="text-xs text-slate-500">BAC defaults to your project&apos;s final cost. Edit any cell — recompute updates indices and forecast.</p>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm text-slate-700 w-32">BAC</span>
            <Input className="h-8 w-48" type="number" value={bacInput} onChange={e => setBacInput(e.target.value)} data-testid="evm-bac-input" />
            <span className="text-xs text-slate-500">— project final cost from Calculator</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Series</th>
                  {Array.from({length: months}).map((_, i) => (
                    <th key={i} className="text-right p-1">M{i+1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <SeriesRow label="PV" arr={pv} update={update(setPv)} testid="evm-pv" />
                <SeriesRow label="EV" arr={ev} update={update(setEv)} testid="evm-ev" />
                <SeriesRow label="AC" arr={ac} update={update(setAc)} testid="evm-ac" />
              </tbody>
            </table>
          </div>
          {summary.TCPI_BAC != null && (
            <div className="mt-3 text-xs text-slate-600">
              TCPI(BAC) = <span className="font-mono">{fmt(summary.TCPI_BAC, 3)}</span> — {summary.TCPI_BAC > 1.05 ? 'tight: requires above-plan efficiency to hit BAC.' : 'achievable to hit BAC.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SeriesRow({ label, arr, update, testid }) {
  return (
    <tr className="border-t">
      <td className="p-1 font-semibold text-slate-700 w-24">
        <Badge variant="outline" className="text-xs">{label}</Badge>
      </td>
      {arr.map((v, i) => (
        <td key={i} className="p-1">
          <Input
            type="number"
            value={v}
            onChange={e => update(i, e.target.value)}
            className="h-7 text-right text-xs px-1"
            data-testid={`${testid}-m${i+1}`}
          />
        </td>
      ))}
    </tr>
  );
}

function Pill({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`w-2 h-12 rounded ${accent}`} />
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}

function evenCum(total, n) {
  const step = total / Math.max(1, n);
  return Array.from({length: n}, (_, i) => Math.round(step * (i + 1)));
}
